import Color from 'color';
import { lFColor0 } from 'pages/components/LFHeader/LFHeader.styled';
import {
  LEFT_SIDE_PANEL_MOTION_MS,
  leftSidePanelBodyMotion,
  leftSidePanelMotionEase,
  leftSidePanelWidthMotion,
} from 'pages/styles';
import styled from 'styled-components';

const ink = '#1a1a1a';
const inkMuted = '#6e6e6e';
const panelRightShadow = '1px 0 0 rgba(17, 24, 39, 0.06), 4px 0 16px rgba(17, 24, 39, 0.04)';
const panelBg = '#fafafa';
const line = Color('#e5e7eb').darken(0.02).toString();
const rowHover = Color(lFColor0).alpha(0.06).toString();

export const Shell = styled.div<{
  isWeb?: boolean,
  $open?: boolean,
  $expandWidth?: boolean,
  $fullyHidden?: boolean,
  $hideBelow: string,
}>`
  ${leftSidePanelWidthMotion}
  box-sizing: border-box;
  background: ${panelBg};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  z-index: 3;
  position: relative;
  box-shadow: ${panelRightShadow};
  ${({ $fullyHidden, isWeb }) => ($fullyHidden && isWeb ? `
    width: 0;
    min-width: 0;
    flex-basis: 0;
    box-shadow: none;
    pointer-events: none;
    opacity: 0;
  ` : '')}
  transition:
    width ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    min-width ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    flex-basis ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    opacity ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    box-shadow ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase};
  @media(max-width: ${({ $hideBelow }) => $hideBelow}) {
    /* Desktop duplicate must fully unmount from layout — left:-100% still peeked. */
    display: ${({ isWeb }) => (isWeb ? 'none' : 'flex')};
    box-shadow: none;
    background: white;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PanelToggleRow = styled.div<{ $collapsed?: boolean, $hideBelow: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: ${({ $collapsed }) => ($collapsed ? '14px 14px 14px 10px' : '14px 14px 12px')};
  box-sizing: border-box;
  border-bottom: 1px solid ${({ $collapsed }) => ($collapsed ? 'transparent' : line)};
  background: ${({ $collapsed }) => ($collapsed ? panelBg : 'white')};
  flex-shrink: 0;
  transition:
    padding ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase},
    border-color 220ms ${leftSidePanelMotionEase},
    background 220ms ${leftSidePanelMotionEase};
  @media(max-width: ${({ $hideBelow }) => $hideBelow}) {
    display: none;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PanelTitle = styled.div<{ $hidden?: boolean }>`
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ink};
  line-height: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  transition: opacity 220ms ${leftSidePanelMotionEase};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PanelToggle = styled.button<{ $expanded?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border: 1px solid ${line};
  border-radius: 8px;
  background: #fff;
  color: ${inkMuted};
  cursor: pointer;
  padding: 0;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  & > svg {
    transition: transform ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase};
    transform: rotate(${({ $expanded }) => ($expanded === false ? 180 : 0)}deg);
  }

  &:hover {
    background: ${rowHover};
    border-color: ${Color(lFColor0).alpha(0.28).toString()};
    color: ${lFColor0};
  }

  @media (prefers-reduced-motion: reduce) {
    & > svg {
      transition: none;
    }
  }
`;

export const PaneBody = styled.div<{ $open?: boolean }>`
  ${leftSidePanelBodyMotion}
`;
